import {
  getLeoUuid,
  LeoUUID,
  getObject,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
import { ChatSurveyMutationResult, ChatSurveyMutationResultDTO } from "./chatSurveyMutationResult";
import { SurveyLocalizedText, SurveyLocalizedTextDTO } from "../assets/surveyLocalizedText";
/* eslint-disable import/export */
export abstract class SubmitChatSurveyRPC implements
  LeoRPC<SubmitChatSurveyRPC.Request, SubmitChatSurveyRPC.Response, SubmitChatSurveyRPC.Errors.Errors>
{
  abstract execute(
    request: SubmitChatSurveyRPC.Request
  ): Promise<LeoRPCResult<SubmitChatSurveyRPC.Response, SubmitChatSurveyRPC.Errors.Errors>>;
}

export namespace SubmitChatSurveyRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    surveyLinkId: string;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isSubmitChatSurveyRPCRequest: boolean = true;
  
    readonly surveyLinkId: LeoUUID;
  
  
    constructor(
    surveyLinkId: LeoUUID
    ) {
      this.surveyLinkId = surveyLinkId;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const surveyLinkId = getLeoUuid(dto, "surveyLinkId", InvalidRequestSurveyLinkIdError);
      return new Request(
        surveyLinkId
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          surveyLinkId: this.surveyLinkId.toDTO()
      };
      return dto;
    }
    }
  
  Request.prototype.toString = function toString(): string {
    return `SubmitChatSurveyRPC(surveyLinkId='${this.surveyLinkId}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {
    result: ChatSurveyMutationResultDTO;
  }
  
  export class Response implements LeoRPCResponse {
    readonly isSubmitChatSurveyResponse: boolean = true;
  
    readonly result: ChatSurveyMutationResult;
  
  
    constructor(
    result: ChatSurveyMutationResult
    ) {
      this.result = result;
    }
  
    static fromDTO(dto: ResponseDTO): Response {
      const result = ChatSurveyMutationResult.fromDTO(getObject(dto, "result", InvalidResponseResultError));
      return new Response(
        result
      );
    }
  
    toDTO(): ResponseDTO {
      const dto: ResponseDTO = {
          result: this.result.toDTO()
      };
      return dto;
    }
    }
  
  Response.prototype.toString = function toString(): string {
    return `SubmitChatSurvey(result='${this.result}')`;
  };
  

  export enum RPCError {
    InvalidSurveyLinkId = "INVALID_SURVEY_LINK_ID",
    SurveyIsClosed = "SURVEY_IS_CLOSED",
    ChatInterfaceNotEnabled = "CHAT_INTERFACE_NOT_ENABLED",
    SubmissionNotReady = "SUBMISSION_NOT_READY"
  }

  export namespace Errors {
    
    export interface InvalidSurveyLinkIdDTO {
      code: string;
    }
    
    export class InvalidSurveyLinkId extends LeoRPCError<RPCError> {
      readonly isInvalidSurveyLinkId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidSurveyLinkId;
        super(code);
      }
    
    
      toDTO(): InvalidSurveyLinkIdDTO {
        const dto: InvalidSurveyLinkIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidSurveyLinkId.prototype.toString = function toString(): string {
      return `SubmitChatSurveyRPC.INVALID_SURVEY_LINK_ID()`;
    };
    
    
    export interface SurveyIsClosedDTO {
      code: string;
      surveyIsClosedTitleText: SurveyLocalizedTextDTO;
      surveyIsClosedDescriptionText: SurveyLocalizedTextDTO;
    }
    
    export class SurveyIsClosed extends LeoRPCError<RPCError> {
      readonly isSurveyIsClosed: boolean = true;
    
      readonly surveyIsClosedTitleText: SurveyLocalizedText;
      readonly surveyIsClosedDescriptionText: SurveyLocalizedText;
    
      constructor(
      surveyIsClosedTitleText: SurveyLocalizedText, 
      surveyIsClosedDescriptionText: SurveyLocalizedText
      ) {
        const code = RPCError.SurveyIsClosed;
        super(code);
        this.surveyIsClosedTitleText = surveyIsClosedTitleText;
        this.surveyIsClosedDescriptionText = surveyIsClosedDescriptionText;
      }
    
      static fromDTO(dto: SurveyIsClosedDTO): SurveyIsClosed {
        const surveyIsClosedTitleText = SurveyLocalizedText.fromDTO(getObject(dto, "surveyIsClosedTitleText", InvalidResponseSurveyIsClosedSurveyIsClosedTitleTextError));
        const surveyIsClosedDescriptionText = SurveyLocalizedText.fromDTO(getObject(dto, "surveyIsClosedDescriptionText", InvalidResponseSurveyIsClosedSurveyIsClosedDescriptionTextError));
        return new SurveyIsClosed(
          surveyIsClosedTitleText,
          surveyIsClosedDescriptionText
        );
      }
    
      toDTO(): SurveyIsClosedDTO {
        const dto: SurveyIsClosedDTO = {
          code: this.code,
          surveyIsClosedTitleText: this.surveyIsClosedTitleText.toDTO(),
          surveyIsClosedDescriptionText: this.surveyIsClosedDescriptionText.toDTO()
        };
        return dto;
      }
    
      }
    
    SurveyIsClosed.prototype.toString = function toString(): string {
      return `SubmitChatSurveyRPC.SURVEY_IS_CLOSED(surveyIsClosedTitleText='${this.surveyIsClosedTitleText}',surveyIsClosedDescriptionText='${this.surveyIsClosedDescriptionText}')`;
    };
    
    
    export interface ChatInterfaceNotEnabledDTO {
      code: string;
    }
    
    export class ChatInterfaceNotEnabled extends LeoRPCError<RPCError> {
      readonly isChatInterfaceNotEnabled: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.ChatInterfaceNotEnabled;
        super(code);
      }
    
    
      toDTO(): ChatInterfaceNotEnabledDTO {
        const dto: ChatInterfaceNotEnabledDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    ChatInterfaceNotEnabled.prototype.toString = function toString(): string {
      return `SubmitChatSurveyRPC.CHAT_INTERFACE_NOT_ENABLED()`;
    };
    
    
    export interface SubmissionNotReadyDTO {
      code: string;
    }
    
    export class SubmissionNotReady extends LeoRPCError<RPCError> {
      readonly isSubmissionNotReady: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.SubmissionNotReady;
        super(code);
      }
    
    
      toDTO(): SubmissionNotReadyDTO {
        const dto: SubmissionNotReadyDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    SubmissionNotReady.prototype.toString = function toString(): string {
      return `SubmitChatSurveyRPC.SUBMISSION_NOT_READY()`;
    };
    

    export type Errors = InvalidSurveyLinkId | SurveyIsClosed | ChatInterfaceNotEnabled | SubmissionNotReady;
    export type ErrorsDTO = InvalidSurveyLinkIdDTO | SurveyIsClosedDTO | ChatInterfaceNotEnabledDTO | SubmissionNotReadyDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidSurveyLinkId:
          return new InvalidSurveyLinkId();
        case RPCError.SurveyIsClosed:
          return SurveyIsClosed.fromDTO(dto as SurveyIsClosedDTO);
        case RPCError.ChatInterfaceNotEnabled:
          return new ChatInterfaceNotEnabled();
        case RPCError.SubmissionNotReady:
          return new SubmissionNotReady();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestSurveyLinkIdError extends SubmitChatSurveyRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSurveyLinkIdError";
  }
}

export class InvalidResponseResultError extends SubmitChatSurveyRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseResultError";
  }
}

export class InvalidResponseInvalidSurveyLinkIdError extends SubmitChatSurveyRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidSurveyLinkIdError";
  }
}

export class InvalidResponseSurveyIsClosedError extends SubmitChatSurveyRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSurveyIsClosedError";
  }
}

export class InvalidResponseSurveyIsClosedSurveyIsClosedTitleTextError extends InvalidResponseSurveyIsClosedError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSurveyIsClosedSurveyIsClosedTitleTextError";
  }
}

export class InvalidResponseSurveyIsClosedSurveyIsClosedDescriptionTextError extends InvalidResponseSurveyIsClosedError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSurveyIsClosedSurveyIsClosedDescriptionTextError";
  }
}
export class InvalidResponseChatInterfaceNotEnabledError extends SubmitChatSurveyRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseChatInterfaceNotEnabledError";
  }
}

export class InvalidResponseSubmissionNotReadyError extends SubmitChatSurveyRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSubmissionNotReadyError";
  }
}

